/**
 * OwnerCAM — Public marketing landing page.
 *
 * Routed at `/` (the root). SEO-optimised with semantic headings, FAQ rich-content,
 * keyword-dense copy targeting general aviation owner-operator pilots looking
 * for maintenance tracking software. No app data is fetched here; the page is
 * pure marketing.
 */
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AirplaneTilt, Wrench, BellRinging, Gear, FileText, ShieldCheck,
  Users, ChartLineUp, ClipboardText, CheckCircle, CaretRight,
  GlobeHemisphereWest, ListChecks, EnvelopeSimple, CurrencyEur,
} from "@phosphor-icons/react";
import Footer from "../components/Footer";
import Disclaimer from "../components/Disclaimer";

const STANDARD = { mo: 30, yr: 288, yrFull: 360 };
const PRO = { mo: 50, yr: 480, yrFull: 600 };

// ─────────────────────────────────────────────────────────────────────────────
// Re-usable building blocks
// ─────────────────────────────────────────────────────────────────────────────
function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`px-6 lg:px-10 py-20 lg:py-28 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.3em] text-amber font-mono mb-4">
      {children}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, testId }) {
  return (
    <div className="group p-6 border border-zinc-800 bg-zinc-950/50 hover:border-amber/50 hover:bg-zinc-950 transition-colors" data-testid={testId}>
      <div className="w-10 h-10 border border-amber/50 flex items-center justify-center mb-4 group-hover:bg-amber/10 transition-colors">
        <Icon size={20} className="text-amber" />
      </div>
      <h3 className="text-base font-medium tracking-tight mb-2 text-zinc-100">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function CheckRow({ children }) {
  return (
    <li className="flex items-start gap-3 text-sm text-zinc-300">
      <CheckCircle size={16} weight="fill" className="text-amber mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Landing
// ─────────────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    // Smooth in-page anchor scroll
    document.documentElement.style.scrollBehavior = "smooth";
    return () => { document.documentElement.style.scrollBehavior = ""; };
  }, []);

  const startTrial = () => navigate("/signup");
  const signIn = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="landing-page">

      {/* ── Top nav ── */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-9 h-9 border border-amber flex items-center justify-center">
              <AirplaneTilt size={20} weight="bold" className="text-amber" />
            </div>
            <div className="leading-tight">
              <div className="text-base font-semibold tracking-tight">OwnerCAM</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 font-mono">Aircraft ownership platform</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-400">
            <a href="#features" className="hover:text-zinc-100">Features</a>
            <a href="#pricing" className="hover:text-zinc-100">Pricing</a>
            <a href="#faq" className="hover:text-zinc-100">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={signIn} className="hidden sm:inline text-sm text-zinc-300 hover:text-zinc-100" data-testid="nav-signin-btn">
              Sign in
            </button>
            <button
              onClick={startTrial}
              className="h-9 px-4 bg-amber text-black font-medium tracking-wide uppercase text-xs hover:bg-[#FFC033] transition-colors"
              data-testid="nav-trial-btn"
            >
              Start free trial
            </button>
          </div>
        </div>
      </header>

      {/* ── 1. Hero ── */}
      <Section id="hero" className="!pt-16 lg:!pt-24 !pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Maintenance tracking · Renewals · ADs · Documents · Cost sharing</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6" data-testid="hero-title">
              Maintenance tracking for <span className="amber">aircraft owner-operators.</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed max-w-xl mb-8" data-testid="hero-sub">
              Track inspections, renewals, parts, documents, ADs, and aircraft ownership
              costs in one simple platform built for general aviation owners.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                onClick={startTrial}
                className="h-12 px-6 bg-amber text-black font-medium tracking-wide uppercase text-sm hover:bg-[#FFC033] transition-colors"
                data-testid="hero-trial-btn"
              >
                Start 90-day free trial
              </button>
              <a href="#pricing" className="h-12 px-6 inline-flex items-center justify-center border border-zinc-700 text-sm text-zinc-200 hover:border-amber hover:text-amber transition-colors" data-testid="hero-pricing-link">
                See pricing
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-500 font-mono uppercase tracking-wider">
              <span>· 90 days free</span>
              <span>· Cancel anytime</span>
              <span>· Auto-converts to paid at trial end</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] border border-zinc-800 overflow-hidden">
              {/* Phase 4F — Cessna 172N hero (Wikimedia Commons, CC) */}
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/Cessna_172N_Skyhawk_%E2%80%98N6182G%E2%80%99_%2828264272488%29.jpg/1280px-Cessna_172N_Skyhawk_%E2%80%98N6182G%E2%80%99_%2828264272488%29.jpg"
                alt="Cessna 172N Skyhawk N6182G"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.45]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/85" />
              <div className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: "radial-gradient(circle at 25% 30%, #FFB000 0%, transparent 35%), radial-gradient(circle at 75% 70%, #FFB000 0%, transparent 35%)",
              }} />
              <div className="relative h-full flex flex-col justify-between p-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Owner dashboard · YR-EXMP</div>
                  <div className="text-sm font-medium tracking-tight text-zinc-200">Cessna 172N · 1980</div>
                </div>
                <div className="space-y-2.5">
                  {[
                    { l: "Annual inspection", v: "Due in 12 days", c: "amber" },
                    { l: "ELT battery", v: "Due in 47 days", c: "zinc-400" },
                    { l: "AD 2024-21-12", v: "Overdue · 3 d", c: "red-400" },
                    { l: "Magneto life remaining", v: "62 h · 18%", c: "amber" },
                    { l: "Insurance renewal", v: "Due 2026-09-04", c: "zinc-400" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between border-l-2 border-amber/40 pl-3 py-1.5">
                      <span className="text-xs text-zinc-300">{r.l}</span>
                      <span className={`text-xs font-mono text-${r.c}`}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── 2. Product overview ── */}
      <Section id="overview" className="border-t border-zinc-900">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Eyebrow>What is OwnerCAM</Eyebrow>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight leading-tight mb-6">
              Know what is due,<br/>what is coming,<br/>and what is missing.
            </h2>
          </div>
          <div className="lg:col-span-7 text-zinc-300 leading-relaxed space-y-4">
            <p>
              OwnerCAM is a maintenance tracking and ownership management platform for
              general aviation aircraft owners. It centralises every recurring date,
              hour, and cycle that an owner-operator pilot has to keep track of —
              annual inspections, 100-hour inspections, transponder and altimeter
              checks, ELT batteries, ARC renewals, insurance, AD compliance, part
              life limits, and aircraft documents — and surfaces them in plain
              English on one dashboard.
            </p>
            <p>
              Built for <strong className="text-zinc-100">owner-operator pilots, partnerships, and small flying groups.</strong> Not
              another MRO/Part-145 tool with steep ramps.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-2">
              <CheckRow>One dashboard per aircraft</CheckRow>
              <CheckRow>Email + in-app reminders</CheckRow>
              <CheckRow>AD search and applicability matching</CheckRow>
              <CheckRow>Parts life on hours, cycles, and calendar</CheckRow>
              <CheckRow>Document storage with expiry tracking</CheckRow>
              <CheckRow>Cost sharing add-on for partnerships</CheckRow>
            </ul>
          </div>
        </div>
      </Section>

      {/* ── 2b. Photo banner — Cessna 172N (Wikimedia Commons, CC) ── */}
      <div className="border-t border-zinc-900 overflow-hidden">
        <div className="relative h-[280px] lg:h-[360px]">
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/80/Cessna_172N_Skyhawk_%E2%80%98N75822%E2%80%99_%2836263391602%29.jpg/1280px-Cessna_172N_Skyhawk_%E2%80%98N75822%E2%80%99_%2836263391602%29.jpg"
            alt="Cessna 172N Skyhawk on the ramp"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          <div className="absolute inset-0 flex items-end pb-10 px-6 lg:px-10">
            <div className="max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber/80 font-mono mb-2">Built by pilots, for pilots</div>
              <div className="text-2xl lg:text-3xl font-semibold tracking-tight text-zinc-100 leading-tight">
                Every owner-operator&apos;s hours, dates, and ADs — in one place, before they bite you.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3. Features grid ── */}
      <Section id="features" className="border-t border-zinc-900">
        <Eyebrow>Built for owner-operators</Eyebrow>
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3" data-testid="features-title">Everything an aircraft owner tracks, in one place.</h2>
        <p className="text-zinc-400 mb-12 max-w-2xl">
          OwnerCAM helps you stay ahead of every renewal, inspection, and AD without
          spreadsheets, calendar reminders, or scattered PDFs.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard
            icon={Wrench} testId="feature-maintenance"
            title="Maintenance tracking"
            body="Annual, 100-hour, progressive, and condition-based inspections — track when each one was last done and when it's due next, by date or by tach time."
          />
          <FeatureCard
            icon={BellRinging} testId="feature-alerts"
            title="Alerts & renewals"
            body="In-app banner plus email reminders at 30, 7, and 0 days for every renewal: ARC, ELT, transponder, altimeter, pitot/static, insurance, registration."
          />
          <FeatureCard
            icon={Gear} testId="feature-parts"
            title="Parts & component life"
            body="Track life-limited parts on hours, cycles, calendar, or absolute date. The earliest-due criterion wins — no more accidental overrun on a forgotten metric."
          />
          <FeatureCard
            icon={FileText} testId="feature-documents"
            title="Aircraft documents"
            body="Store W&B, POH/AFM, insurance, registration, ARC, logbook scans, 8130s, release-to-service certs. Expiry tracking baked in."
          />
          <FeatureCard
            icon={ShieldCheck} testId="feature-ad"
            title="AD & compliance support"
            body="FAA, EASA, TCCA, and CAA-UK ADs matched to your make/model/year/engine/STCs. Mark-complied flow auto-fills date + tach + cycles."
          />
          <FeatureCard
            icon={ChartLineUp} testId="feature-forecast"
            title="Maintenance forecast"
            body="Looking 25 hours and 30 days ahead, what will be due? Plan your next shop visit before the squawks pile up."
          />
          <FeatureCard
            icon={ClipboardText} testId="feature-workorders"
            title="Work orders"
            body="Bundle a shop visit. Link parts, maintenance items, and ADs. Auto-snapshot last-done on completion. Auto-create the expense on invoicing."
          />
          <FeatureCard
            icon={GlobeHemisphereWest} testId="feature-ai"
            title="AI manual assistant"
            body="Upload your POH/AFM and ask it questions. The assistant cites page numbers from your actual aircraft documentation."
          />
          <FeatureCard
            icon={Users} testId="feature-costshare"
            title="Cost sharing (Pro)"
            body="Co-owner allocation, flight cost tracking, fixed vs variable costs, payment tracking. Available in OwnerCAM Pro."
          />
        </div>
      </Section>

      {/* ── 4. Alerts & renewals deep-dive ── */}
      <Section id="alerts" className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow>Never miss a date</Eyebrow>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-6">
              Reminders that actually understand aviation.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              OwnerCAM tracks each item on the metric it's regulated by — calendar
              date, tach time, hobbs, cycles, or any combination — and warns you on
              the schedule that matters: 30 days, 7 days, day-of.
            </p>
            <ul className="space-y-2.5">
              <CheckRow>Annual / 100-hour / progressive inspection cycles</CheckRow>
              <CheckRow>ARC renewals (and CAMO oversight where required)</CheckRow>
              <CheckRow>ELT battery + survival kit expiry</CheckRow>
              <CheckRow>Transponder · altimeter · pitot/static · static port checks</CheckRow>
              <CheckRow>Insurance / registration / pilot medical</CheckRow>
              <CheckRow>AD recurring compliance dates</CheckRow>
            </ul>
          </div>
          <div className="border border-zinc-800 bg-[#0A0A0A] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-4">Upcoming · 30 days</div>
            <div className="space-y-3">
              {[
                { d: "Today", t: "AD 2024-21-12 (recurring)", st: "OVERDUE", c: "red" },
                { d: "+ 3 days", t: "Transponder check", st: "URGENT", c: "amber" },
                { d: "+ 12 days", t: "Annual inspection", st: "WARNING", c: "amber" },
                { d: "+ 18 days", t: "ELT battery", st: "WARNING", c: "amber" },
                { d: "+ 27 days", t: "Pitot/static check", st: "OK", c: "zinc-500" },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-4 text-xs">
                  <span className="w-20 text-zinc-500 font-mono">{r.d}</span>
                  <span className="flex-1 text-zinc-200">{r.t}</span>
                  <span className={`font-mono text-[10px] px-2 py-0.5 border border-${r.c}-500/40 text-${r.c}-300`}>{r.st}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── 5. Parts & document tracking ── */}
      <Section id="parts-docs" className="border-t border-zinc-900">
        <div className="grid md:grid-cols-2 gap-10">
          <div className="p-8 border border-zinc-800">
            <Eyebrow>Part life tracking</Eyebrow>
            <h3 className="text-2xl font-semibold tracking-tight mb-4">Hours · cycles · calendar · date</h3>
            <p className="text-zinc-400 leading-relaxed mb-5">
              Track every life-limited component with as many limits as it has.
              Engine-category parts reference engine hours; airframe parts reference
              tach time. The earliest-due metric drives the alert.
            </p>
            <ul className="space-y-2 text-sm">
              <CheckRow>Engine TBO + calendar TBO</CheckRow>
              <CheckRow>Propeller TBO + overhaul date</CheckRow>
              <CheckRow>Hose, battery, vacuum pump life</CheckRow>
              <CheckRow>Magneto IRAN cycles</CheckRow>
            </ul>
          </div>
          <div className="p-8 border border-zinc-800">
            <Eyebrow>Document management</Eyebrow>
            <h3 className="text-2xl font-semibold tracking-tight mb-4">19 aviation-specific document types</h3>
            <p className="text-zinc-400 leading-relaxed mb-5">
              Airworthiness, registration, insurance, W&B, POH/AFM, maintenance and
              parts manuals, logbook scans, AD reports, inspection records, ARC,
              8130-3, Form 1, CRS, contracts, and more.
            </p>
            <ul className="space-y-2 text-sm">
              <CheckRow>Expiry tracking + colour-coded warnings</CheckRow>
              <CheckRow>Upload from desktop or mobile</CheckRow>
              <CheckRow>AI extraction for insurance/ARC/registration</CheckRow>
              <CheckRow>Chat with your manuals (Pro tier supported)</CheckRow>
            </ul>
          </div>
        </div>
      </Section>

      {/* ── 6. AD compliance ── */}
      <Section id="ad-compliance" className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>AD & compliance support</Eyebrow>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-6">
              ADs matched to <span className="amber">your exact aircraft.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-4">
              Add a new AD once. OwnerCAM matches its applicability against every
              aircraft in your workspace by manufacturer, model series, year range,
              engine, and installed STCs. The applicable ones surface on each
              aircraft's compliance list automatically.
            </p>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Supports FAA, EASA, TCCA (Transport Canada), CAA-UK, and ANAC ADs.
              Mark-complied auto-fills date, tach time, and cycles from the aircraft
              record — recurring ADs schedule their next due date automatically.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              <CheckRow>Applicability matching engine</CheckRow>
              <CheckRow>Recurring AD compliance scheduling</CheckRow>
              <CheckRow>Fleet-wide compliance heatmap</CheckRow>
              <CheckRow>One-click mark-complied with snapshot</CheckRow>
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-zinc-800 bg-[#0A0A0A] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-4">Compliance · YR-EXMP</div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="border border-red-500/40 bg-red-500/10 p-3 text-center">
                  <div className="text-2xl font-mono text-red-300">1</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-200">Overdue</div>
                </div>
                <div className="border border-amber/40 bg-amber/10 p-3 text-center">
                  <div className="text-2xl font-mono text-amber">3</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber/80">Urgent</div>
                </div>
                <div className="border border-emerald-500/40 bg-emerald-500/10 p-3 text-center">
                  <div className="text-2xl font-mono text-emerald-300">12</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-200/80">Complied</div>
                </div>
              </div>
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider">16 ADs applicable to your aircraft</div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── 7. Cost sharing (Pro plan) ── */}
      <Section id="cost-sharing" className="border-t border-zinc-900">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>Cost sharing · Pro plan</Eyebrow>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
              For partnerships and flying groups.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              The cost-sharing module is included in OwnerCAM Pro. Track who flew
              when, how much they owe, and what each reserve fund needs to be topped
              up to. Built for closed groups — not commercial rental.
            </p>
            <button
              onClick={startTrial}
              className="h-11 px-5 bg-amber text-black font-medium tracking-wide uppercase text-xs hover:bg-[#FFC033] transition-colors"
              data-testid="costshare-trial-btn"
            >
              Try Pro free for 90 days
            </button>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            <FeatureCard
              icon={Users} testId="pro-coowner"
              title="Co-owner allocation"
              body="Per-member named-on-insurance, contract dates, recency rules. Booking allowed only for active members."
            />
            <FeatureCard
              icon={CurrencyEur} testId="pro-flightcost"
              title="Flight cost tracking"
              body="Variable €/hr + annual fixed + onboarding + instructor + buffer. Auto-generated settlement on every flight log."
            />
            <FeatureCard
              icon={ChartLineUp} testId="pro-reports"
              title="Cost-sharing reports"
              body="Year-end reconciliation with per-pilot net (contributed vs fair share) and expenses by category."
            />
            <FeatureCard
              icon={ListChecks} testId="pro-payments"
              title="Payment tracking"
              body="Settlement state machine pending → sent → confirmed. Reserves auto-funded on confirmation."
            />
          </div>
        </div>
      </Section>

      {/* ── 8. Pricing ── */}
      <Section id="pricing" className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="text-center mb-12">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-3" data-testid="pricing-title">
            Two plans. Per aircraft. 90 days free.
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Try every feature free for 90 days. Billing only starts if you decide to
            continue. Annual prepayment saves 20% (non-refundable).
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Standard */}
          <div className="border border-zinc-800 bg-[#0A0A0A] p-7 flex flex-col" data-testid="plan-standard">
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-1">Standard</div>
              <h3 className="text-2xl font-semibold tracking-tight">Maintenance tracking</h3>
            </div>
            <div className="mb-6 pb-6 border-b border-zinc-800">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-semibold tracking-tight" data-testid="standard-monthly-price">€{STANDARD.mo}</span>
                <span className="text-sm text-zinc-500">/aircraft/month</span>
              </div>
              <div className="mt-2 text-xs text-zinc-500">
                or <span className="text-zinc-300 font-mono">€{STANDARD.yr}/year</span>
                <span className="ml-2 inline-block px-2 py-0.5 border border-amber/40 bg-amber/10 text-amber font-mono text-[10px] tracking-wider uppercase" data-testid="standard-annual-badge">20% off</span>
              </div>
              <div className="mt-1 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">Annual prepayment is non-refundable</div>
            </div>
            <ul className="space-y-2.5 mb-7 flex-1">
              <CheckRow>Aircraft profile & registration tracking</CheckRow>
              <CheckRow>Maintenance & inspection tracking</CheckRow>
              <CheckRow>Renewal reminders (30/7/0d)</CheckRow>
              <CheckRow>Parts & component life</CheckRow>
              <CheckRow>Calendar / hour / cycle alerts</CheckRow>
              <CheckRow>Aircraft document storage</CheckRow>
              <CheckRow>AD tracking & compliance support</CheckRow>
              <CheckRow>Maintenance forecast</CheckRow>
              <CheckRow>Owner dashboard</CheckRow>
              <CheckRow>Email alerts & basic reports</CheckRow>
            </ul>
            <button
              onClick={startTrial}
              className="h-11 w-full border border-amber text-amber font-medium tracking-wide uppercase text-xs hover:bg-amber hover:text-black transition-colors"
              data-testid="standard-cta"
            >
              Start free with Standard
            </button>
          </div>

          {/* Pro */}
          <div className="border-2 border-amber bg-[#0A0A0A] p-7 relative flex flex-col" data-testid="plan-pro">
            <div className="absolute -top-3 right-7 px-3 py-1 bg-amber text-black text-[10px] font-mono uppercase tracking-wider">
              Includes cost sharing
            </div>
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber font-mono mb-1">Pro</div>
              <h3 className="text-2xl font-semibold tracking-tight">Maintenance + cost sharing</h3>
            </div>
            <div className="mb-6 pb-6 border-b border-zinc-800">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-semibold tracking-tight" data-testid="pro-monthly-price">€{PRO.mo}</span>
                <span className="text-sm text-zinc-500">/aircraft/month</span>
              </div>
              <div className="mt-2 text-xs text-zinc-500">
                or <span className="text-zinc-300 font-mono">€{PRO.yr}/year</span>
                <span className="ml-2 inline-block px-2 py-0.5 border border-amber/40 bg-amber/10 text-amber font-mono text-[10px] tracking-wider uppercase" data-testid="pro-annual-badge">20% off</span>
              </div>
              <div className="mt-1 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">Annual prepayment is non-refundable</div>
            </div>
            <ul className="space-y-2.5 mb-7 flex-1">
              <li className="text-sm text-zinc-300 flex items-start gap-3"><CheckCircle size={16} weight="fill" className="text-amber mt-0.5 shrink-0" /><span><strong className="text-zinc-100">Everything in Standard</strong></span></li>
              <CheckRow>Aircraft cost-sharing module</CheckRow>
              <CheckRow>Co-owner cost allocation</CheckRow>
              <CheckRow>Flight cost tracking</CheckRow>
              <CheckRow>Fixed & variable cost tracking</CheckRow>
              <CheckRow>Cost-sharing reports & reconciliation</CheckRow>
              <CheckRow>Owner usage reports</CheckRow>
              <CheckRow>Payment tracking</CheckRow>
              <CheckRow>Shared ownership expense visibility</CheckRow>
            </ul>
            <button
              onClick={startTrial}
              className="h-11 w-full bg-amber text-black font-medium tracking-wide uppercase text-xs hover:bg-[#FFC033] transition-colors"
              data-testid="pro-cta"
            >
              Start free with Pro
            </button>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-zinc-500 font-mono uppercase tracking-wider">
          All prices in EUR · VAT shown at checkout where applicable
        </div>
      </Section>

      {/* ── 9. Free trial CTA ── */}
      <Section id="trial" className="border-t border-zinc-900">
        <div className="border border-amber/30 bg-gradient-to-br from-amber/5 via-transparent to-amber/5 p-10 lg:p-14 text-center">
          <Eyebrow>90-day free trial</Eyebrow>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight mb-4">
            Try OwnerCAM <span className="amber">free for 90 days.</span>
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-7 leading-relaxed">
            Create a workspace, add your aircraft, upload your manuals, and start
            tracking. We don't ask for a card until day 90. Cancel anytime before
            then and you'll never be charged.
          </p>
          <button
            onClick={startTrial}
            className="h-12 px-7 bg-amber text-black font-medium tracking-wide uppercase text-sm hover:bg-[#FFC033] transition-colors"
            data-testid="bottom-trial-btn"
          >
            Start 90-day free trial
          </button>
          <div className="mt-5 text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
            Standard or Pro · Per aircraft · Cancel anytime
          </div>
        </div>
      </Section>

      {/* ── 10. FAQ ── */}
      <Section id="faq" className="border-t border-zinc-900 bg-zinc-950/40">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight">Questions, answered.</h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Still wondering something? Reach out and we'll get back to you.
            </p>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {[
              { q: "How does the 90-day free trial work?", a: "Sign up with email or Google, create a workspace, and add your first aircraft. You'll have full access to all features in your selected plan for 90 days. We'll show you a banner reminding you to add a payment method before the trial ends. If you don't add one, your workspace is paused — your data is never deleted." },
              { q: "What's the difference between Standard and Pro?", a: "Standard (€30/aircraft/month) covers everything an owner-operator needs: maintenance tracking, inspections, parts, ADs, documents, alerts, forecast, and the owner dashboard. Pro (€50/aircraft/month) adds the cost-sharing module — co-owner allocation, flight cost tracking, settlements, reconciliation, payment tracking." },
              { q: "Is cost sharing available in Standard?", a: "No. The cost-sharing module is exclusive to OwnerCAM Pro. You can upgrade from Standard to Pro any time and it activates immediately." },
              { q: "How does annual billing work?", a: "Annual prepayment is €288/aircraft/year for Standard or €480/aircraft/year for Pro — a 20% discount on the monthly rate. Annual prepayments are non-refundable. You can downgrade to monthly billing on renewal." },
              { q: "Do I need a CAMO if I'm a private owner?", a: "Usually no. Under EU rules a CAMO is only required when (a) you're operating commercially (AOC / Part-CAT), (b) the aircraft is a complex motor-powered aircraft, or (c) you're flying under a commercial specialised / ATO operation. Non-commercial, non-complex aircraft (the vast majority of private GA — Cessna 172, PA-28, Diamond DA40, etc.) can usually be owner-managed under Part-ML. OwnerCAM is built for exactly that segment: private owner-operators of non-complex, non-commercial aircraft. If your aircraft IS complex or you ARE operating commercially, you'll need a contracted CAMO — OwnerCAM still helps you stay on top of the data, but the CAMO remains the legal compliance owner." },
              { q: "Does OwnerCAM replace my CAMO, mechanic, or inspector?", a: "No. OwnerCAM is a tracking and alerting tool. It does not replace the aircraft owner/operator's legal responsibilities, an approved maintenance program, a certified CAMO where one is required, a licensed mechanic, a Part-145 organization, an A&P/IA, or any applicable aviation authority requirement. Always verify maintenance, AD compliance, and airworthiness decisions with qualified personnel and official source documents." },
              { q: "Which ADs are supported?", a: "FAA Airworthiness Directives, EASA ADs, TCCA (Transport Canada) ADs, CAA-UK ADs, and ANAC ADs. The applicability engine matches on manufacturer, model series, year range, engine, and installed STCs." },
              { q: "Can I have multiple aircraft on one workspace?", a: "Yes. Pricing is per aircraft, and you can add or remove aircraft at any time. Removing an aircraft reduces the next billing cycle's invoice." },
              { q: "What happens if I cancel?", a: "On cancellation, your workspace remains read-only until the end of your paid billing period, then is paused. Your data is preserved — you can re-subscribe any time. Annual prepayments are not refunded." },
              { q: "Can I export my data?", a: "Yes. PDF exports are built in for reconciliation, settlements, and the logbook. CSV export is on the roadmap." },
              { q: "Is OwnerCAM safe for sensitive aircraft records?", a: "Yes. Every workspace is fully isolated — there's no cross-tenant access. Sessions are signed, CSRF-protected, and bank details are scrubbed from audit logs. Documents are stored encrypted at rest." },
            ].map((f, i) => (
              <details key={i} className="group border border-zinc-800 bg-[#0A0A0A] p-5" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-sm font-medium text-zinc-100 pr-4">{f.q}</span>
                  <CaretRight size={16} className="text-zinc-500 group-open:rotate-90 transition-transform shrink-0" />
                </summary>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 11. Safety / responsibility disclaimer ── */}
      <Section id="disclaimer" className="border-t border-zinc-900">
        <Disclaimer />
      </Section>

      <Footer />
    </div>
  );
}
